import OpenAI from 'openai';

export type AIPolicyLevel = 'ai-free' | 'ai-scaffolded' | 'ai-integrated';

export interface LearningOutcome {
  title: string;
  bloom: string;
  description: string;
}

export interface PolicyGenerationParams {
  taskDescription: string;
  aiPolicyLevel: AIPolicyLevel;
  outcomes: LearningOutcome[];
  // New optional fields to match the richer prompt
  title?: string;
  type?: string;
  modality?: string;
  classSize?: string;
  timeAllocation?: string;
  assessmentContext?: string;
}

class OpenAIService {
  private client: OpenAI | null = null;

  /**
   * Get the API key from environment variable
   */
  private getApiKey(): string | null {
    const envKey = import.meta.env.VITE_OPENAI_API_KEY;
    if (envKey && envKey !== 'sk-your-api-key-here') {
      return envKey;
    }
    return null;
  }

  /**
   * Check if API key is configured
   */
  hasApiKey(): boolean {
    return !!this.getApiKey();
  }

  /**
   * Initialize or get the OpenAI client
   */
  private getClient(): OpenAI {
    const apiKey = this.getApiKey();
    
    if (!apiKey) {
      throw new Error('OpenAI API key not configured. Please set VITE_OPENAI_API_KEY in your .env file.');
    }

    if (!this.client) {
      this.client = new OpenAI({
        apiKey,
        dangerouslyAllowBrowser: true,
      });
    }

    return this.client;
  }

  /**
   * Generate an AI policy for a specific assessment task
   */
  async generateAIPolicy(params: PolicyGenerationParams): Promise<string> {
    const client = this.getClient();

    const policyLevelGuidelines = this.getPolicyLevelGuidelines(params.aiPolicyLevel);
    
    const prompt = `You are an expert in assessment design and academic integrity. Generate a comprehensive Generative AI use policy for the following assessment task.

ASSESSMENT TASK DESCRIPTION:
Title: ${params.title || 'Untitled Assignment'}
Type: ${params.type || 'Assessment'}
Instructions: ${params.taskDescription}
${params.modality ? `Modality: ${params.modality}` : ''}
${params.classSize ? `Class Size: ${params.classSize}` : ''}
${params.timeAllocation ? `Time Allocation: ${params.timeAllocation}` : ''}
${params.assessmentContext ? `Context: ${params.assessmentContext}` : ''}

AI POLICY LEVEL: ${params.aiPolicyLevel}

LINKED LEARNING OUTCOMES:
${params.outcomes.map(o => `- ${o.title} (${o.bloom}): ${o.description}`).join('\n')}

POLICY LEVEL GUIDELINES:
${policyLevelGuidelines}

Generate a complete AI policy statement that includes the following sections formatted in Markdown:

1. **Course Outcome**: State which learning outcome(s) this assessment addresses and why they're important
2. **Purpose for Learning**: Explain the pedagogical intent - what skills/knowledge students will develop
3. **AI Use Guidelines Table**: Create a clear table with these columns:
   - Stage (Before drafting, During work, After completion)
   - Permitted AI Use
   - Not Permitted AI Use
4. **Alignment with Course Outcome**: Explain how the AI policy choices support the learning objectives and ensure meaningful skill development

Format Requirements:
- Use proper Markdown syntax (headings with ##, bold with **, tables with |)
- Write in clear, professional academic language appropriate for university students
- Be specific and actionable - students should know exactly what they can and cannot do
- Keep the focus on learning and academic integrity
- Make the connection between AI use boundaries and learning goals explicit

Generate the policy now:`;

    try {
      const response = await client.chat.completions.create({
        model: 'gpt-5.1',
        messages: [
          {
            role: 'system',
            content: 'You are an expert educational consultant specializing in academic integrity and AI use policies in higher education. You create clear, actionable policies that help students understand appropriate AI use while maintaining academic integrity.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
      });

      const content = response.choices[0]?.message?.content;
      
      if (!content) {
        throw new Error('No response received from OpenAI');
      }

      // Strip markdown code fences if present
      let cleanContent = content;
      if (cleanContent.startsWith('```')) {
        cleanContent = cleanContent.replace(/^```(?:markdown)?\s*/, '');
      }
      if (cleanContent.endsWith('```')) {
        cleanContent = cleanContent.replace(/\s*```$/, '');
      }

      return cleanContent.replace(/<br\s*\/?>/gi, ' ');
    } catch (error: unknown) {
      console.error('OpenAI API error:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('API key')) {
          throw new Error('Invalid API key. Please check your VITE_OPENAI_API_KEY in the .env file.');
        }
        if (error.message.includes('quota') || error.message.includes('rate')) {
          throw new Error('API rate limit or quota exceeded. Please try again later or check your OpenAI account.');
        }
        throw error;
      }
      
      throw new Error('Failed to generate AI policy. Please try again.');
    }
  }

  /**
   * Get policy level guidelines text
   */
  private getPolicyLevelGuidelines(level: AIPolicyLevel): string {
    switch (level) {
      case 'ai-free':
        return `AI-Free Assignment:
- Students complete work independently without AI assistance
- Develops independent thought, critical thinking, and original expression
- AI tools NOT permitted for: idea generation, outlining, paraphrasing, translating, grammar checks, or editing
- Only human-authored sources explicitly approved by instructor are allowed`;

      case 'ai-scaffolded':
        return `AI-Scaffolded Assignment:
- AI tools permitted at specific stages (planning and revision) only
- Core work must be completed independently
- PERMITTED BEFORE DRAFTING: Brainstorming topics, outlining sections, organizing writing schedule
- PERMITTED AFTER COMPLETE DRAFT: Feedback on structure, coherence, and alignment with rubric
- NOT PERMITTED: Generating thesis statements, arguments, full paragraphs, rewriting content, or grammar correction via AI`;

      case 'ai-integrated':
        return `AI-Integrated Assignment:
- Students work collaboratively with AI tools throughout the assignment
- Must maintain control, ethical standards, and transparency
- PERMITTED IN PLANNING: Generate timelines, explore topic areas, structure initial goals
- PERMITTED IN EXECUTION: Generate code variations, translate key terms, simulate data for testing
- PERMITTED IN REVIEW: Solicit feedback on argument strength, clarity, or completeness
- NOT PERMITTED: Submitting unedited AI-generated answers/analysis, concealing AI use, omitting fact-checking
- REQUIRED: Transparency about AI use, fact-checking all AI outputs, maintaining authorship control`;
    }
  }
}

export const openaiService = new OpenAIService();
